import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  CircleUserRound,
  PlusCircle,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Role } from "@/types/user";
import { UserProfile } from "@/types/user-profile";
import { UserFormData, userFormSchema } from "../utils/userSchemas";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { hasRole, isPlatformAdminRole } from "@/utils/accessControl";
import {
  useUserCreate,
  useUserFilterOptions,
  useUserUpdate,
} from "../hooks/useUsers";
import {
  FormField,
  SelectField,
} from "@/components/pages/reservations/guests/components/form/FormFields";
import { Hotel } from "@/types/hotel";
import ImageSelector from "@/components/pages/reservations/guests/components/form/ImageSelector";
import LanguageSelect from "@/components/common/LanguageSelect";
import {
  useCustomRoles,
  useUserCustomRoleAssignments,
} from "@/components/pages/users/hooks/useCustomRoles";
import { Badge } from "@/components/ui/badge";
import { formatCustomRoleError } from "@/components/pages/users/utils/customRoleErrors";

interface UserFormProps {
  hotels: Hotel[];
  onBack: () => void;
  user?: UserProfile;
  isEditing?: boolean;
  isCreating?: boolean;
  tenantId?: string;
}

const validateCreateCredentials = (
  data: UserFormData,
  resolvedRole: Role,
  t: any,
  errors: string[],
) => {
  if (resolvedRole !== Role.STAFF && !data.email?.trim()) {
    errors.push(t("users.form.emailRequired", "Valid email is required"));
  }
  if (
    resolvedRole !== Role.STAFF &&
    (!data.password || data.password.length < 8)
  ) {
    errors.push(t("users.form.passwordMinLength"));
  }
};

const validateStaffCredentials = (
  data: UserFormData,
  t: any,
  errors: string[],
) => {
  if (data.password && data.pin) {
    errors.push(
      t(
        "users.form.staffPasswordPinConflict",
        "Staff users should use either PIN or password authentication, not both",
      ),
    );
  }
  if (!data.password && !data.pin) {
    errors.push(
      t(
        "users.form.staffPasswordOrPinRequired",
        "Staff users require either password or PIN",
      ),
    );
  }
};

const validateFormData = (
  data: UserFormData,
  isEditing: boolean,
  resolvedRole: Role,
  t: any,
) => {
  const errors: string[] = [];

  if (!isEditing) {
    validateCreateCredentials(data, resolvedRole, t, errors);
  }

  if (resolvedRole === Role.FRONT_DESK || resolvedRole === Role.STAFF) {
    if (!data.hotelId || data.hotelId.trim() === "") {
      errors.push(t("users.form.hotelIdRequired"));
    }
  }

  if (!isEditing && resolvedRole === Role.STAFF) {
    validateStaffCredentials(data, t, errors);
  }

  return {
    success: errors.length === 0,
    errors: errors,
  };
};

const getTitle = (
  isEditing: boolean,
  isPreview: boolean,
  isCreating: boolean,
  t: any,
) => {
  if (isEditing) return t("users.form.editUser");
  if (isPreview) return t("users.form.userProfile");
  if (isCreating) return t("users.form.addNewUser");
  return "";
};

const getHotelPlaceholder = (
  isCreating: boolean,
  selectedRole: Role,
  t: any,
) => {
  if (isCreating && selectedRole === Role.FRONT_DESK) {
    return t("users.form.selectHotelRequired");
  }
  if (isCreating && selectedRole === Role.HOTEL_ADMIN) {
    return t("users.form.selectHotelOptional");
  }
  return t("users.form.selectHotel");
};

const getSubmitButtonText = (
  isSubmitting: boolean,
  isEditing: boolean,
  t: any,
) => {
  if (isSubmitting) {
    return isEditing ? t("users.form.updating") : t("users.form.creating");
  }
  return isEditing ? t("users.form.updateUser") : t("users.form.createUser");
};

const isOwnProfileEdit = (
  isEditing: boolean,
  viewerId?: string,
  userId?: string,
): boolean => Boolean(isEditing && userId && viewerId === userId);

const renderCreateCredentialsFields = ({
  isCreating,
  selectedRole,
  watch,
  t,
  register,
  errors,
  isInteractive,
  showPassword,
  setShowPassword,
}: any) => {
  if (!isCreating) {
    return null;
  }

  return (
    <>
      <FormField
        value={watch("password")}
        label={t("users.form.password")}
        id="password"
        type={showPassword ? "text" : "password"}
        register={register}
        error={errors.password?.message}
        isEdit={isInteractive}
        rightElement={
          <div className="absolute right-3 flex items-center">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          </div>
        }
        className="pr-10"
      />
      {selectedRole === Role.STAFF ? (
        <FormField
          value={watch("pin")}
          label={t("users.form.pin", "PIN")}
          id="pin"
          register={register}
          error={errors.pin?.message}
          isEdit={isInteractive}
        />
      ) : null}
    </>
  );
};

const renderPositionField = ({
  isCreating,
  t,
  positions = [],
  value,
  setValue,
  error,
  register,
}: any) => {
  if (!isCreating) {
    return null;
  }
  return (
    <SelectField
      containerClassName="col-span-2"
      id="position"
      label={t("users.form.position")}
      options={positions.map((position: string) => ({
        value: position,
        label: position,
      }))}
      value={value || ""}
      onValueChange={(next: string) => setValue("position", next)}
      placeholder={
        positions.length === 0
          ? t("users.form.noPositions")
          : t("users.form.selectPosition")
      }
      error={error}
      isEdit
      register={register}
    />
  );
};

const renderRoleFields = ({
  isInteractive,
  t,
  selectedRoleMode,
  setValue,
  setSelectedRoleOption,
  errors,
  register,
  customRoles,
  watch,
  selectedCustomRole,
  systemRoleOptions,
  selectedRoleOption,
  handleSystemRoleChange,
  assignedCustomRoleName,
}: any) => {
  if (!isInteractive) {
    const roleDisplayOptions = assignedCustomRoleName
      ? [{ value: assignedCustomRoleName, label: assignedCustomRoleName }]
      : systemRoleOptions;
    const roleDisplayValue = assignedCustomRoleName || selectedRoleOption;
    return (
      <SelectField
        containerClassName="col-span-2"
        id="role"
        label={t("users.form.role")}
        options={roleDisplayOptions}
        value={roleDisplayValue}
        onValueChange={(value: string) => {
          handleSystemRoleChange(value);
        }}
        placeholder={t("users.form.selectRole")}
        error={errors.role?.message}
        isEdit={isInteractive}
        register={register}
      />
    );
  }

  return (
    <>
      <SelectField
        containerClassName="col-span-1"
        id="roleMode"
        label={t("users.form.roleMode", "Role mode")}
        options={[
          { value: "system", label: t("users.form.systemRole", "System role") },
          { value: "custom", label: t("users.form.customRole", "Custom role") },
        ]}
        value={selectedRoleMode}
        onValueChange={(value: string) => {
          const nextMode = value as "system" | "custom";
          setValue("roleMode", nextMode);
          setValue("customRoleId", undefined);
          setValue("role", Role.HOTEL_ADMIN);
          setSelectedRoleOption(Role.HOTEL_ADMIN);
          setValue("hotelId", "");
        }}
        isEdit={isInteractive}
        register={register}
      />
      {selectedRoleMode === "custom" ? (
        <div className="col-span-1 flex flex-col gap-1">
          <SelectField
            id="customRoleId"
            label={t("users.form.customRole", "Custom role")}
            options={[...customRoles]
              .sort((a, b) =>
                a.baseRole === b.baseRole
                  ? a.name.localeCompare(b.name)
                  : a.baseRole.localeCompare(b.baseRole),
              )
              .map((customRole) => ({
                value: customRole.id,
                label: `${customRole.name} — ${customRole.baseRole}`,
              }))}
            value={watch("customRoleId") || ""}
            onValueChange={(value: string) => {
              setValue("customRoleId", value);
              const next = customRoles.find((role: any) => role.id === value);
              if (next) {
                setValue("role", next.baseRole);
                setSelectedRoleOption(next.baseRole);
                setValue("hotelId", "");
              }
            }}
            placeholder={t("users.form.selectCustomRole", "Select custom role")}
            error={errors.customRoleId?.message}
            isEdit={isInteractive}
            register={register}
          />
          {selectedCustomRole ? (
            <Badge
              variant="secondary"
              className="self-start text-xs font-medium"
            >
              {t("users.form.customRoleResolvedSystemRole", {
                defaultValue: "System role: {{role}}",
                role: selectedCustomRole.baseRole,
              })}
            </Badge>
          ) : null}
        </div>
      ) : (
        <SelectField
          containerClassName="col-span-1"
          id="role"
          label={t("users.form.role")}
          options={systemRoleOptions}
          value={selectedRoleOption}
          onValueChange={(value: string) => {
            handleSystemRoleChange(value);
          }}
          placeholder={t("users.form.selectRole")}
          error={errors.role?.message}
          isEdit={isInteractive}
          register={register}
        />
      )}
    </>
  );
};

export const UserForm: React.FC<UserFormProps> = ({
  user,
  onBack,
  hotels,
  isEditing = false,
  isCreating = false,
}) => {
  const { t } = useTranslation();
  const { userProfile: viewerProfile } = useAuthenticatedUser();
  const canAssignTenantAdmin = isPlatformAdminRole(viewerProfile);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | undefined>(
    undefined,
  );
  const [showPassword, setShowPassword] = useState(false);
  const isInteractive = isEditing || isCreating;
  const isPreview = !isEditing && !isCreating;
  const isEditingOwnProfile = isOwnProfileEdit(
    isEditing,
    viewerProfile?.id,
    user?.id,
  );
  const isRoleInteractive = isInteractive && !isEditingOwnProfile;
  const isTenantAdminViewer = hasRole(viewerProfile, Role.TENANT_ADMIN);
  const { data: customRoles = [] } = useCustomRoles(isInteractive);
  const { data: roleAssignments = [] } = useUserCustomRoleAssignments(
    user?.id || "",
    isEditing || (isPreview && isTenantAdminViewer),
  );
  const assignedCustomRoleName = roleAssignments
    .map((assignment) => assignment.customRoleName)
    .join(", ");

  const safeUserRole =
    user?.role && user.role !== "SUPER_ADMIN" ? user.role : Role.HOTEL_ADMIN;

  const baseSystemRoleOptions = [
    { value: Role.HOTEL_ADMIN, label: t("users.form.hotelAdmin") },
    { value: Role.FRONT_DESK, label: t("users.form.frontDesk") },
    // { value: Role.STAFF, label: t("users.form.staff", "Staff") },
  ];
  const allSystemRoleOptions = baseSystemRoleOptions.some(
    (option) => option.value === safeUserRole,
  )
    ? baseSystemRoleOptions
    : [
        {
          value: safeUserRole,
          label:
            safeUserRole === Role.TENANT_ADMIN
              ? t("users.form.tenantAdmin", "Tenant admin")
              : safeUserRole,
        },
        ...baseSystemRoleOptions,
      ];
  const systemRoleOptions = canAssignTenantAdmin
    ? allSystemRoleOptions
    : allSystemRoleOptions.filter((opt) => opt.value !== Role.TENANT_ADMIN);

  const { data: filterOptions } = useUserFilterOptions(undefined, isCreating);

  const { handleUserSubmit: handleUserCreate } = useUserCreate();
  const { handleUserSubmit: handleUserUpdate } = useUserUpdate(user?.id || "");

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      username: "",
      password: "",
      email: user?.email ?? "",
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      phone: user?.phone ?? "",
      role: safeUserRole,
      customRoleId: undefined,
      roleMode: "system",
      hotelId: user?.hotelId,
      pin: "",
      preferredLanguage: user?.preferredLanguage ?? "en",
    },
  });
  const [selectedRoleOption, setSelectedRoleOption] =
    useState<string>(safeUserRole);
  const selectedRoleMode = watch("roleMode") || "system";
  const assignedCustomRoleId = roleAssignments[0]?.customRoleId;
  const selectedCustomRole = customRoles.find(
    (customRole) => customRole.id === watch("customRoleId"),
  );
  const resolvedRole = selectedCustomRole?.baseRole ?? watch("role");

  useEffect(() => {
    if (selectedRoleMode !== "custom") return;
    if (!selectedCustomRole) return;
    if (selectedRoleOption === selectedCustomRole.baseRole) return;
    setSelectedRoleOption(selectedCustomRole.baseRole);
    setValue("role", selectedCustomRole.baseRole);
  }, [selectedCustomRole, selectedRoleMode, selectedRoleOption, setValue]);

  useEffect(() => {
    if (!user) {
      return;
    }
    setValue("email", user.email || "");
    setValue("firstName", user.firstName || "");
    setValue("lastName", user.lastName || "");
    setValue("phone", user.phone || "");
    setValue("role", safeUserRole);

    setValue("hotelId", user.hotelId);
    setValue("photoId", user.photoId);
    setValue("preferredLanguage", user.preferredLanguage || "en");
    if (!isEditing) {
      setValue("customRoleId", undefined);
      setValue("roleMode", "system");
    }
    setSelectedRoleOption(safeUserRole);
  }, [isEditing, safeUserRole, user, setValue]);

  useEffect(() => {
    if (!user) {
      setSelectedRoleOption(Role.HOTEL_ADMIN);
      setValue("roleMode", "system");
    }
  }, [user, setValue]);

  useEffect(() => {
    if (!isEditing || !user) {
      return;
    }
    if (assignedCustomRoleId) {
      const assignedCustomRole = customRoles.find(
        (role) => role.id === assignedCustomRoleId,
      );
      setValue("roleMode", "custom");
      setValue("customRoleId", assignedCustomRoleId);
      if (assignedCustomRole?.baseRole) {
        setValue("role", assignedCustomRole.baseRole);
        setSelectedRoleOption(assignedCustomRole.baseRole);
      }
      return;
    }
    setValue("roleMode", "system");
    setValue("customRoleId", undefined);
  }, [assignedCustomRoleId, customRoles, isEditing, setValue, user]);

  const saveUser = async (data: UserFormData) => {
    if (!isEditing) {
      await handleUserCreate(data, selectedPhotoFile || undefined);
      toast.success(t("users.form.userCreatedSuccessfully"));
    } else {
      await handleUserUpdate(
        data,
        selectedPhotoFile || undefined,
        user?.photoId,
        !isEditingOwnProfile,
      );
      toast.success(t("users.form.userUpdatedSuccessfully"));
    }
  };

  const onSubmit = async (data: UserFormData) => {
    const validationResult = validateFormData(data, isEditing, resolvedRole, t);

    if (!validationResult.success) {
      toast.error(validationResult.errors.join(", "));
      return;
    }

    try {
      await saveUser(data);
      onBack();
    } catch (error) {
      const knownRoleNamesById = new Map(
        customRoles.map((customRole) => [customRole.id, customRole.name]),
      );
      const detail = formatCustomRoleError(error, knownRoleNamesById);
      let errorMessage = detail;
      if (detail === (error instanceof Error ? error.message : "")) {
        errorMessage = isEditing
          ? t("users.form.failedToUpdateUser")
          : t("users.form.failedToCreateUser");
      }
      toast.error(errorMessage);
      console.error("Error saving user:", error);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setValue("photoId", imageUrl);
      setSelectedPhotoFile(file);
    }
  };

  const selectedImage = watch("photoId");
  const selectedHotel = watch("hotelId");
  const selectedRole = resolvedRole;
  const handleSystemRoleChange = (value: string) => {
    setSelectedRoleOption(value);
    setValue("customRoleId", undefined);
    setValue("role", value as Role);
    if ((value as Role) !== selectedRole) {
      setValue("hotelId", "");
    }
  };

  const title = getTitle(isEditing, isPreview, isCreating, t);

  return (
    <div className="p-12">
      <Button
        onClick={onBack}
        variant={"icon-text"}
        className={"border-0 text-[24px]"}
      >
        <ArrowLeft className="size-6" />
        {t("users.form.back")}
      </Button>

      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
        <h1 className="text-center text-4xl">{title}</h1>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="grid grid-cols-2 gap-x-4 gap-y-6"
        >
          <div className={"col-span-2 mb-6 flex items-center justify-center"}>
            <ImageSelector
              defaultIcon={
                <CircleUserRound className={"size-[72px] stroke-1"} />
              }
              overlayContent={
                <>
                  {isInteractive && (
                    <div
                      className={
                        "absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full bg-white"
                      }
                    >
                      <PlusCircle className={"size-5"} />
                    </div>
                  )}
                </>
              }
              onImageSelect={handleImageUpload}
              selectedImage={selectedImage}
              className="mb-4"
              imageClassName={"size-[72px] rounded-full"}
            />
          </div>

          {isCreating ? (
            <FormField
              value={watch("email")}
              label={t("users.form.email")}
              id="email"
              type="email"
              register={register}
              error={errors.email?.message}
              isEdit={isInteractive}
            />
          ) : null}
          {renderCreateCredentialsFields({
            isCreating,
            selectedRole,
            watch,
            t,
            register,
            errors,
            isInteractive,
            showPassword,
            setShowPassword,
          })}
          <FormField
            value={watch("firstName")}
            label={t("users.form.firstName")}
            id="firstName"
            register={register}
            error={errors.firstName?.message}
            isEdit={isInteractive}
          />
          <FormField
            value={watch("lastName")}
            label={t("users.form.lastName")}
            id="lastName"
            register={register}
            error={errors.lastName?.message}
            isEdit={isInteractive}
          />

          {!isCreating ? (
            <FormField
              value={watch("email")}
              label={t("users.form.email")}
              id="email"
              type="email"
              register={register}
              error={errors.email?.message}
              isEdit={isInteractive}
              isCopy={isPreview}
            />
          ) : null}

          <FormField
            value={watch("phone")}
            label={t("users.form.phone")}
            id="phone"
            register={register}
            error={errors.phone?.message}
            isEdit={isInteractive}
            isCopy={isPreview}
          />

          {renderRoleFields({
            isInteractive: isRoleInteractive,
            t,
            selectedRoleMode,
            setValue,
            setSelectedRoleOption,
            errors,
            register,
            customRoles,
            watch,
            selectedCustomRole,
            systemRoleOptions,
            selectedRoleOption,
            handleSystemRoleChange,
            assignedCustomRoleName,
          })}

          {isEditingOwnProfile ? (
            <p className="text-muted-foreground col-span-2 text-sm">
              {t(
                "users.form.ownRoleReadOnly",
                "You cannot change your own role or access rights.",
              )}
            </p>
          ) : null}

          {renderPositionField({
            isCreating,
            t,
            positions: filterOptions?.positions,
            value: watch("position"),
            setValue,
            error: errors.position?.message,
            register,
          })}

          <SelectField
            containerClassName="col-span-2"
            id="hotelId"
            label={
              isCreating && selectedRole === Role.FRONT_DESK
                ? t("users.form.hotelRequired")
                : t("users.form.hotel")
            }
            required={isCreating && selectedRole === Role.FRONT_DESK}
            options={hotels.map((hotel) => ({
              value: hotel.id,
              label: hotel.name,
            }))}
            value={
              selectedHotel || (isPreview ? t("users.form.notSelected") : "")
            }
            onValueChange={(value: string) => setValue("hotelId", value)}
            placeholder={getHotelPlaceholder(isCreating, selectedRole, t)}
            error={errors.hotelId?.message}
            isEdit={isInteractive}
            register={register}
          />

          <LanguageSelect
            className="col-span-2"
            label={t("users.form.preferredLanguage")}
            value={watch("preferredLanguage")}
            onChange={(val) => setValue("preferredLanguage", val)}
            disabled={isPreview}
            error={errors.preferredLanguage?.message}
          />

          {isInteractive && (
            <div className="col-span-2 flex justify-end gap-4 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onBack}
                disabled={isSubmitting}
              >
                {t("users.form.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {getSubmitButtonText(isSubmitting, isEditing, t)}
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
